// CertDashboard.js
import { useEffect, useMemo, useState } from "react";
import api from "services/api";

// helper local
const unwrapList = (data) =>
  Array.isArray(data) ? data
  : Array.isArray(data?.data) ? data.data
  : Array.isArray(data?.page) ? data.page
  : Array.isArray(data?.results) ? data.results
  : [];

async function fetchAllCertified({
  recordLabelIds = [],
  allowAll = false,
}) {
  const pageSize = 1000;
  let all = [];
  let offset = 0;
  let total = Infinity;

  const baseParams = {};

  const normalizedRecordLabelIds = Array.isArray(recordLabelIds)
    ? [
        ...new Set(
          recordLabelIds
            .filter(
              (id) =>
                id !== null &&
                id !== undefined &&
                id !== ""
            )
            .map(String)
        ),
      ]
    : [];

  if (!normalizedRecordLabelIds.length && !allowAll) {
    return [];
  }

  normalizedRecordLabelIds.forEach((id, index) => {
    baseParams[`record_label_id[$in][${index}]`] = id;
  });

  while (offset < total) {
    const { data } = await api.get("/certified", {
      params: {
        __limit: pageSize,
        __offset: offset,
        ...baseParams,
      },
    });

    const page = unwrapList(data);
    const curTotal = Number(
      data?.total ?? data?.count ?? page.length
    );

    if (Number.isFinite(curTotal)) {
      total = curTotal;
    }

    all = all.concat(page);

    if (!page.length || page.length < pageSize) {
      break;
    }

    offset += page.length;
  }

  if (!all.length) {
    offset = 0;
    total = Infinity;

    while (offset < total) {
      const { data } = await api.get("/certified", {
        params: {
          limit: pageSize,
          offset,
          ...baseParams,
        },
      });

      const page = unwrapList(data);
      const curTotal = Number(
        data?.total ?? data?.count ?? page.length
      );

      if (Number.isFinite(curTotal)) {
        total = curTotal;
      }

      all = all.concat(page);

      if (!page.length || page.length < pageSize) {
        break;
      }

      offset += page.length;
    }
  }

  if (!all.length) {
    const { data } = await api.get("/certified", {
      params: baseParams,
    });

    all = unwrapList(data);
  }

  return all;
}

/** intenta cargar catálogo de sellos (varios endpoints posibles) */
async function fetchRecordLabels() {
  const pageSize = 1000;
  let all = [];
  let offset = 0;

  for (;;) {
    const { data } = await api.get("/recordLabel", {
      params: {
        __limit: pageSize,
        __offset: offset,
      },
    });

    const page = unwrapList(data);

    if (!page.length) {
      break;
    }

    all = all.concat(page);

    if (page.length < pageSize) {
      break;
    }

    offset += page.length;
  }

  return all;
}

function getRecordLabelGroupIds(label) {
  if (Array.isArray(label?.groups) && label.groups.length) {
    return label.groups
      .map((g) =>
        typeof g === "object" && g !== null
          ? g.id ?? g._id
          : g
      )
      .filter(
        (id) =>
          id !== null &&
          id !== undefined &&
          id !== ""
      )
      .map(String);
  }

  // compatibilidad con sellos antiguos
  if (
    label?.group_id !== null &&
    label?.group_id !== undefined &&
    label?.group_id !== ""
  ) {
    return [String(label.group_id)];
  }

  return [];
}

async function fetchRecordLabelById(id) {
  if (!id) return null;
  try {
    const { data } = await api.get("/recordLabel", { params: { id } });

    // puede venir como objeto directo o paginado
    if (data && !Array.isArray(data)) {
      // si trae {page:[...]} usamos unwrapList
      const list = unwrapList(data);
      if (list.length) return list[0];
      // si trae el objeto directo con id/name
      if (data.id || data._id) return data;
    }

    const list = unwrapList(data);
    return list[0] || null;
  } catch (e) {
    console.warn("fetchRecordLabelById failed:", id, e?.response?.status, e?.message);
    return null;
  }
}


const toMonthKey = (d) => {
  const x = new Date(d);
  if (Number.isNaN(+x)) return "";
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`;
};
const statusNum = (s) => (typeof s === "boolean" ? (s ? 1 : 0) : Number(s));

function inRangeByDate(it, from, to) {
  const raw = it.created_at;
  if (!raw) return true;
  const d = new Date(raw);
  if (Number.isNaN(+d)) return true;

  const f = from ? new Date(from + "T00:00:00") : null;
  const t = to   ? new Date(to   + "T23:59:59") : null;
  if (f && d < f) return false;
  if (t && d > t) return false;
  return true;
}

export default function useCertDashboardData({
    groupIds = [],
    from,
    to,
    allowAll = false,
  }) {
  
  const [state, setState] = useState({
    loading: true,
    err: "",
    items: [],
  });
  
  const [labelMap, setLabelMap] = useState({}); // id -> name
  const [recordLabels, setRecordLabels] = useState([]);
  const [recordLabelsReady, setRecordLabelsReady] = useState(false);

   // carga cat. de sellos una vez (para nombres)
  useEffect(() => {
    let cancel = false;

    (async () => {
      try {
        const labels = await fetchRecordLabels();

        if (cancel) return;

        setRecordLabels(labels || []);

        const map = {};

        (labels || []).forEach((x) => {
          const id = String(
            x.id ?? x._id ?? x.value ?? ""
          );

          const name =
            x.name ??
            x.nombre ??
            x.title ??
            `#${id}`;

          if (id) {
            map[id] = name;
          }
        });

        setLabelMap(map);
        setRecordLabelsReady(true);
      } catch (_) {
        if (!cancel) {
          setRecordLabels([]);
          setRecordLabelsReady(true);
        }
      }
    })();

    return () => {
      cancel = true;
    };
  }, []);

  const allowedRecordLabelIds = useMemo(() => {
  if (
    allowAll &&
    (!groupIds || groupIds.length === 0)
  ) {
    return [];
  }

  const normalizedGroupIds =
    Array.isArray(groupIds)
      ? [...new Set(groupIds.map(String))]
      : [];

  if (!normalizedGroupIds.length) {
    return [];
  }

  return (recordLabels || [])
    .filter((label) => {
      const labelGroupIds =
        getRecordLabelGroupIds(label);

      return labelGroupIds.some((groupId) =>
        normalizedGroupIds.includes(String(groupId))
      );
    })
    .map((label) =>
      String(label.id ?? label._id ?? "")
    )
    .filter(Boolean);
}, [
  groupIds,
  recordLabels,
  allowAll,
]);

const recordLabelKey = useMemo(
  () =>
    [...allowedRecordLabelIds]
      .map(String)
      .sort()
      .join("-"),
  [allowedRecordLabelIds]
);
  
  // carga certificaciones
  useEffect(() => {
    let cancel = false;

    (async () => {
      if (!allowAll && !recordLabelsReady) {
        return;
      }

      setState((s) => ({
        ...s,
        loading: !s.items?.length,
        err: "",
      }));

      try {
        const items = await fetchAllCertified({
          recordLabelIds: allowedRecordLabelIds,
          allowAll:
            allowAll &&
            (!groupIds || groupIds.length === 0),
        });

        if (!cancel) {
          setState({
            loading: false,
            err: "",
            items,
          });
        }
      } catch (e) {
        if (!cancel) {
          setState({
            loading: false,
            err:
              e?.response?.data?.message ||
              e?.message ||
              String(e),
            items: [],
          });
        }
      }
    })();

    return () => {
      cancel = true;
    };
  }, [
    recordLabelKey,
    recordLabelsReady,
    allowAll,
  ]);

  const {
    kpis, barChart, lineChart, latest, topStreams,
    countThisMonth, filteredCount, barItems
    , missingLabelIds
  } = useMemo(() => {
    const all   = state.items || [];
    const items = all.filter((it) => inRangeByDate(it, from, to));

  // KPIs (histórico, no filtrado por fechas)
  const kpis = {
    total:    all.length,
    accepted: all.filter((x) => statusNum(x.status) === 1).length,
    pending:  all.filter((x) => ![1,2].includes(statusNum(x.status))).length,
    rejected: all.filter((x) => statusNum(x.status) === 2).length,
  };

    // buckets por mes para gráficas
// buckets por mes para gráficas
const months = {};
items.forEach((it) => {
  const key = toMonthKey(it.created_at);
  if (!key) return;
  months[key] ||= { total: 0, accepted: 0, pending: 0, rejected: 0 };
  months[key].total += 1;
  const st = statusNum(it.status);
  if (st === 1) months[key].accepted += 1;
  else if (st === 2) months[key].rejected += 1;
  else months[key].pending += 1;
});

// helper para mes anterior: "YYYY-MM" -> "YYYY-(MM-1)"
const prevMonthOf = (key) => {
  const [Y, M] = key.split("-").map(Number);
  const d = new Date(Y, (M || 1) - 1, 1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

let labels = Object.keys(months).sort();

// si solo hay 1 mes, agregamos el anterior con ceros para que se vea la línea
if (labels.length === 1) {
  const p = prevMonthOf(labels[0]);
  months[p] ||= { total: 0, accepted: 0, pending: 0, rejected: 0 };
  labels = [p, ...labels];
}

const barChart = {
  labels,
  datasets: { label: "Certificaciones", data: labels.map((k) => months[k].total) },
};
const lineChart = {
  labels,
  datasets: [
    { label: "Aceptadas",  data: labels.map((k) => months[k].accepted), color: "success" },
    { label: "Pendientes", data: labels.map((k) => months[k].pending),  color: "info"    },
    { label: "Rechazadas", data: labels.map((k) => months[k].rejected), color: "error"   },
  ],
};


    // últimas (sin recorte)
    const latest = [...items].sort(
      (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
    );

    // Conteo de este mes
    const now  = new Date();
    const y    = now.getFullYear();
    const m    = now.getMonth();
    const countThisMonth = items.filter((it) => {
      const d = new Date(it.created_at || 0);
      return !Number.isNaN(+d) && d.getFullYear() === y && d.getMonth() === m;
    }).length;

    // Top por Streams (usa filtrado; si queda vacío, cae a histórico)
    //const baseForTop = items.length ? items : all;
    const baseForTop    = items.length ? items : all;
    const topStreams = [...baseForTop]
      .filter((x) => x.sale_units != null)
      .sort((a, b) => Number(b.sale_units) - Number(a.sale_units))
      .slice(0, 6)
      .map((x) => ({
        id: x.id ?? x._id,
        title: `${x.artist ?? "—"} - ${x.title ?? "—"}`,
        value: Number(x.sale_units) || 0,
      }));

    // === Top 4 SELLOS por conteo en el período (items para ReportsBarChart) ===
    //const baseForCounts = items.length ? items : all;
    const baseForCounts = items.length ? items : all;
    const counts = {};
    const missingIds = new Set();
    baseForCounts.forEach((it) => {
    const rawId =
    it.record_label_id ?? it.recordLabelId ?? null;
    const rawName =
    it.record_label_name ??
    it.recordLabelName ??
    it.record_label ??       // a veces esto ya viene como nombre
    it.recordLabel ??
    it.label_name ??
    it.sello ??
    null;
    const id = rawId != null ? String(rawId) : "";
    const nameGuess = rawName || (id ? `Sello #${id}` : "—");
    const key = id || nameGuess; // si no hay id, agrupa por nombre
    if (id && !rawName && !labelMap[id]) missingIds.add(id);
    if (!key) return;
    counts[key] ||= { id, name: nameGuess, count: 0 };
    counts[key].count += 1;
    });

    const top4 = Object.values(counts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 4)
      .map((x) => ({
        name: labelMap[x.id] || x.name || "—",
        count: x.count,
      }));

    const max = top4.reduce((m, x) => Math.max(m, x.count), 0) || 1;
    const colorSeq = ["info", "primary", "warning", "success"];

    const barItems = top4.map((x, i) => ({
      icon: { color: colorSeq[i % colorSeq.length], component: "library_music" },
      label: x.name,
      progress: { content: String(x.count), percentage: Math.round((x.count / max) * 100) },
    }));

    return {
      kpis, barChart, lineChart, latest, topStreams,
      countThisMonth, filteredCount: items.length,
      barItems,
      missingLabelIds: Array.from(missingIds),
    };
  }, [state.items, from, to, labelMap]);

  useEffect(() => {
    let cancel = false;
  
    (async () => {
      const ids = (missingLabelIds || []).filter(Boolean);
  
      if (!ids.length) return;
  
      // evita repetir fetch si ya están
      const toFetch = ids.filter((id) => !labelMap[String(id)]);
      if (!toFetch.length) return;
  
      const entries = await Promise.all(
        toFetch.map(async (id) => {
          const x = await fetchRecordLabelById(id);
          if (!x) return null;
          const key = String(x.id ?? x._id ?? id);
          const name = x.name ?? x.nombre ?? x.title ?? null;
          return name ? [key, name] : null;
        })
      );
  
      if (cancel) return;
  
      const patch = {};
      entries.filter(Boolean).forEach(([k, v]) => (patch[k] = v));
  
      if (Object.keys(patch).length) {
        setLabelMap((prev) => ({ ...prev, ...patch }));
      }
    })();
  
    return () => {
      cancel = true;
    };
  }, [missingLabelIds, labelMap]);
  

  return {
    loading: state.loading, err: state.err,
    kpis, barChart, lineChart, latest, topStreams,
    countThisMonth, filteredCount,
    barItems,
    missingLabelIds,
  };
}
